import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { buildSrcSet, getSizesForContext, getWidthsForContext, buildResponsiveUrl } from '@/lib/imageSrcset';
import { getOptimalQuality, shouldLoadImage, getConnectionAwareWidths } from '@/lib/connectionAware';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  widths?: number[];
  fetchPriority?: 'high' | 'low' | 'auto';
  quality?: number;
  context?: 'hero' | 'content' | 'card' | 'small' | 'custom';
  noPlaceholder?: boolean; // Skip loading placeholder for critical images like logos
  skipOptimization?: boolean; // Skip CDN proxy, load original directly
}

const OptimizedImage = ({ 
  src, 
  alt, 
  className, 
  width, 
  height, 
  sizes, 
  widths, 
  fetchPriority = 'auto', 
  quality,
  context = 'custom',
  noPlaceholder = false,
  skipOptimization = false
}: OptimizedImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [useFallback, setUseFallback] = useState(false);
  const [useStaticallyFallback, setUseStaticallyFallback] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(true);

  // Connection-aware optimization
  const finalContext = context === 'custom' ? 'content' : context;
  const optimalQuality = quality || getOptimalQuality(context);
  const optimalWidths = getConnectionAwareWidths(widths || getWidthsForContext(finalContext));

  useEffect(() => {
    const canLoad = shouldLoadImage(fetchPriority === 'high' ? 'high' : 'low');
    setShouldLoad(canLoad);
  }, [fetchPriority]);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    if (!useStaticallyFallback && !useFallback) {
      // Try Statically fallback first
      setUseStaticallyFallback(true);
      setUseFallback(false);
      setIsLoading(true);
    } else if (useStaticallyFallback && !useFallback) {
      // Try original image fallback
      setUseFallback(true);
      setUseStaticallyFallback(false);
      setIsLoading(true);
    } else {
      setIsLoading(false);
      setHasError(true);
    }
  };

  return (
    <div className={cn("relative overflow-hidden", className)}>
      {/* Blur-up placeholder - skip for critical images */}
      {!noPlaceholder && isLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-muted/50 to-muted/20">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/3 to-primary/6 animate-pulse" />
        </div>
      )}
      
      {hasError ? (
        <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
          <span>Image failed to load</span>
        </div>
      ) : (
        <>
          {shouldLoad && (
            <img
              src={useFallback || skipOptimization ? encodeURI(src) : buildResponsiveUrl(src, { 
                width: width || 800, 
                quality: optimalQuality, 
                provider: useStaticallyFallback ? 'statically' : 'weserv',
                context: finalContext,
                format: 'webp',
                skipOptimization
              })}
              srcSet={useFallback || skipOptimization ? undefined : buildSrcSet(src, optimalWidths, optimalQuality, useStaticallyFallback ? 'statically' : undefined, finalContext, skipOptimization)}
              sizes={useFallback || skipOptimization ? undefined : sizes || getSizesForContext(finalContext)}
              alt={alt}
              width={width}
              height={height}
              loading={fetchPriority === 'high' ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={fetchPriority}
              onLoad={handleLoad}
              onError={handleError}
              className={cn(
                "w-full h-full object-cover",
                noPlaceholder ? "" : "transition-opacity duration-300",
                noPlaceholder || !isLoading ? "opacity-100" : "opacity-0"
              )}
            />
          )}
          {!shouldLoad && (
            <div className="w-full h-full bg-muted rounded-lg flex items-center justify-center text-muted-foreground text-sm">
              Image loading disabled for slow connection
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizedImage;